// drawOla cloud — in-memory mock provider.
//
// Persists state in sessionStorage so reloads keep the illusion within a tab.
// No network, no OAuth. Used by every CloudProviderId in Phase 1 so UI copy
// can say "Google Drive" while behavior is simulated.

import { packEnvelope } from "../../envelope";
import { NotFoundError, TokenExpiredError } from "../../errors";

import type {
  AuthState,
  CloudAccount,
  CloudFileMeta,
  CloudProviderId,
  CloudStorageProvider,
  DrawolaEnvelope,
} from "../../types";

type StoredFile = {
  id: string;
  name: string;
  version: string;
  modifiedTime: number;
  envelope: DrawolaEnvelope;
};

type PersistedState = {
  signedIn: boolean;
  account: CloudAccount | null;
  files: Record<string, StoredFile>;
};

const STORAGE_KEY_PREFIX = "drawOla_cloud_mock_v1";
const MOCK_ACCOUNT: CloudAccount = {
  email: "you@drawOla.mock",
  displayName: "drawOla Mock",
};
const MIME_TYPE = "application/vnd.drawOla+json";

const DISPLAY_NAMES: Record<CloudProviderId, string> = {
  googleDrive: "Google Drive",
  dropbox: "Dropbox",
  oneDrive: "OneDrive",
  pCloud: "pCloud",
  nextcloud: "Nextcloud",
};

function seedFiles(): Record<string, StoredFile> {
  const now = Date.now();
  const seeds: StoredFile[] = [
    {
      id: "mock-file-demo-1",
      name: "Welcome to drawOla Cloud",
      version: "v1",
      modifiedTime: now - 86_400_000,
      envelope: packEnvelope({
        name: "Welcome to drawOla Cloud",
        elements: [],
        appState: {},
        files: {},
      }),
    },
    {
      id: "mock-file-demo-2",
      name: "Quick sketch",
      version: "v1",
      modifiedTime: now - 3_600_000,
      envelope: packEnvelope({
        name: "Quick sketch",
        elements: [],
        appState: {},
        files: {},
      }),
    },
  ];
  const out: Record<string, StoredFile> = {};
  for (const f of seeds) {
    out[f.id] = f;
  }
  return out;
}

function bumpVersion(prev: string): string {
  const match = /^v(\d+)$/.exec(prev);
  const n = match ? parseInt(match[1], 10) : 0;
  return `v${n + 1}`;
}

function toMeta(f: StoredFile): CloudFileMeta {
  return {
    providerFileId: f.id,
    name: f.name,
    modifiedTime: f.modifiedTime,
    version: f.version,
    sizeBytes: JSON.stringify(f.envelope).length,
  };
}

function newId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return `mock-${crypto.randomUUID()}`;
    }
  } catch {
    // ignore
  }
  return `mock-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createMockProvider(
  id: CloudProviderId,
): CloudStorageProvider {
  const storageKey = `${STORAGE_KEY_PREFIX}:${id}`;

  const load = (): PersistedState => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) {
        return { signedIn: false, account: null, files: {} };
      }
      const parsed = JSON.parse(raw) as PersistedState;
      if (!parsed.files || typeof parsed.files !== "object") {
        parsed.files = {};
      }
      return parsed;
    } catch {
      return { signedIn: false, account: null, files: {} };
    }
  };

  const save = (s: PersistedState) => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(s));
    } catch {
      // sessionStorage may be disabled; ignore
    }
  };

  let state = load();

  const listeners = new Set<(s: AuthState) => void>();
  const currentAuthState = (): AuthState =>
    state.signedIn && state.account
      ? { status: "signed-in", account: state.account }
      : { status: "signed-out" };
  const emit = (s: AuthState) => {
    for (const l of listeners) {
      try {
        l(s);
      } catch {
        // one bad listener should not break the rest
      }
    }
  };

  const requireAuth = () => {
    if (!state.signedIn) {
      throw new TokenExpiredError();
    }
  };

  return {
    id,
    displayName: DISPLAY_NAMES[id],
    mimeType: MIME_TYPE,

    async init() {
      // nothing to load; state already hydrated above
    },

    async authenticate(opts) {
      if (state.signedIn && state.account) {
        return state.account;
      }
      if (opts?.interactive === false) {
        throw new Error("Silent auth not supported by mock when signed out");
      }
      emit({ status: "signing-in" });
      await new Promise((r) => setTimeout(r, 500));
      // seed on first sign-in
      if (Object.keys(state.files).length === 0) {
        state = { signedIn: true, account: MOCK_ACCOUNT, files: seedFiles() };
      } else {
        state = { ...state, signedIn: true, account: MOCK_ACCOUNT };
      }
      save(state);
      emit(currentAuthState());
      return MOCK_ACCOUNT;
    },

    async signOut() {
      state = { signedIn: false, account: null, files: {} };
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        // ignore
      }
      emit(currentAuthState());
    },

    getAuthState() {
      return currentAuthState();
    },

    subscribeAuth(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    async listFiles() {
      requireAuth();
      return Object.values(state.files)
        .sort((a, b) => b.modifiedTime - a.modifiedTime)
        .map(toMeta);
    },

    /** Metadata only, no envelope returned. */
    async statFile(fileId) {
      requireAuth();
      const f = state.files[fileId];
      if (!f) {
        throw new NotFoundError();
      }
      return toMeta(f);
    },

    async readFile(fileId) {
      requireAuth();
      const f = state.files[fileId];
      if (!f) {
        throw new NotFoundError();
      }
      return { meta: toMeta(f), envelope: f.envelope };
    },

    async writeFile(args) {
      requireAuth();
      const now = Date.now();
      if (args.providerFileId) {
        const prev = state.files[args.providerFileId];
        if (!prev) {
          throw new NotFoundError();
        }
        const next: StoredFile = {
          ...prev,
          name: args.name,
          version: bumpVersion(prev.version),
          modifiedTime: now,
          envelope: args.envelope,
        };
        state.files[next.id] = next;
        save(state);
        return toMeta(next);
      }
      const id = newId();
      const next: StoredFile = {
        id,
        name: args.name,
        version: "v1",
        modifiedTime: now,
        envelope: args.envelope,
      };
      state.files[id] = next;
      save(state);
      return toMeta(next);
    },

    async deleteFile(fileId) {
      requireAuth();
      delete state.files[fileId];
      save(state);
    },

    async renameFile(fileId, newName) {
      requireAuth();
      const f = state.files[fileId];
      if (!f) {
        throw new NotFoundError();
      }
      const next: StoredFile = {
        ...f,
        name: newName,
        version: bumpVersion(f.version),
        modifiedTime: Date.now(),
        envelope: { ...f.envelope, name: newName, updatedAtClient: Date.now() },
      };
      state.files[fileId] = next;
      save(state);
      return toMeta(next);
    },
  };
}
